import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaExpand, FaMapMarkerAlt } from 'react-icons/fa';

import image1 from '../assets/image1.png';
import image2 from '../assets/image2.png';
import image3 from '../assets/image3.png';
import image4 from '../assets/image4.png';
import heroImage from '../assets/hero_image.png';
import tImage1 from '../assets/t-image1.png';
import treadmillImg from '../assets/treadmill.png';
import spinBikesImg from '../assets/Spin-Bikes.png';
import squatRackImg from '../assets/Olympic-Standard.png';
import benchStationImg from '../assets/Bench-Stations.png';
import cableCrossoverImg from '../assets/Dual-Pulley-Cable.png';
import smithMachineImg from '../assets/Guided-Path-Smith-Machine.png';
import legPressImg from '../assets/Leg-Press-Hack-Squat-Combo.png';
import latPulldownImg from '../assets/Lat-Pulldown.png';
import dumbbellsImg from '../assets/Rubber-Coated-Hex-Dumbbells.png';
import kettlebellsImg from '../assets/Kettlebells.png';

const galleryImages = [
  { src: treadmillImg, title: 'Commercial Cardio Suite', category: 'Cardio', unit: 'Unit 1 & Unit 2' },
  { src: squatRackImg, title: 'Olympic Squat Rack Station', category: 'Equipment', unit: 'All 3 Units' },
  { src: image1, title: 'Main Heavy Strength Floor', category: 'Facility', unit: 'Unit 1 Chettipalayam' },
  { src: cableCrossoverImg, title: 'Dual-Pulley Cable Arena', category: 'Equipment', unit: 'Unit 2 Pappampatti' },
  { src: image2, title: 'Free Weights & Dumbbells Arena', category: 'Facility', unit: 'Unit 3 Peedampalli' },
  { src: legPressImg, title: '45° Heavy Leg Press', category: 'Equipment', unit: 'All 3 Units' },
  { src: heroImage, title: 'Athlete Transformation', category: 'Athletes', unit: 'Community' },
  { src: benchStationImg, title: 'Tri-Angle Bench Press Bay', category: 'Equipment', unit: 'All 3 Units' },
  { src: image3, title: 'Group HIIT & Cardio Zone', category: 'Cardio', unit: 'Unit 1 & Unit 2' },
  { src: dumbbellsImg, title: 'Hex Dumbbell Complete Rack', category: 'Equipment', unit: 'All 3 Units' },
  { src: kettlebellsImg, title: 'Functional Kettlebells & Slams', category: 'Functional', unit: 'Unit 2 & Unit 3' },
  { src: latPulldownImg, title: 'Lat Pulldown & Low Row', category: 'Equipment', unit: 'All 3 Units' },
  { src: smithMachineImg, title: 'Guided Smith Machine Bay', category: 'Equipment', unit: 'All 3 Units' },
  { src: spinBikesImg, title: 'High-RPM Studio Spin Bikes', category: 'Cardio', unit: 'All 3 Units' },
  { src: image4, title: 'Olympic Power Barbell Area', category: 'Facility', unit: 'All 3 Units' },
  { src: tImage1, title: 'Member Body Transformation', category: 'Athletes', unit: 'Member Spotlight' },
];

const categories = ['All', 'Equipment', 'Cardio', 'Facility', 'Functional', 'Athletes'];

const Gallery = () => {
  const [filter, setFilter] = useState('All');
  const [lightbox, setLightbox] = useState(null);

  const transition = { type: 'spring', duration: 2.5 };

  const filteredImages = filter === 'All'
    ? galleryImages
    : galleryImages.filter(img => img.category === filter);

  return (
    <div className="px-4 md:px-12 py-12 text-white overflow-hidden max-w-7xl mx-auto flex flex-col gap-12 min-h-screen">
      {/* ── 1. HEADER ── */}
      <motion.div
        className="text-center max-w-3xl mx-auto"
        initial={{ opacity: 0, y: -30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={transition}
      >
        <span className="inline-block bg-red-600/15 border border-red-500/30 text-red-500 text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4 shadow-lg">
          IDOL FITNESS ZONE • Visual Showcase
        </span>
        <div className="text-3xl sm:text-5xl md:text-6xl font-black uppercase tracking-tight mb-4 leading-none">
          <span className="text-transparent [-webkit-text-stroke:_1.5px_white]">GYM & GEAR </span>
          <span className="text-red-500">GALLERY</span>
        </div>
        <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
          A visual tour of the machines, power cages, free weight arenas, and high-energy atmosphere across our 3 units in Coimbatore.
        </p>
      </motion.div>

      {/* ── 2. FILTER PILLS ── */}
      <div className="flex flex-wrap justify-center gap-2 max-w-4xl mx-auto">
        {categories.map((cat) => (
          <button
            key={cat}
            className={`px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wider transition-all cursor-pointer border ${
              filter === cat
                ? 'bg-red-600 border-red-500 text-white shadow-lg shadow-red-600/30 scale-105'
                : 'bg-[#181a24]/90 border-white/10 text-gray-400 hover:bg-white/10 hover:text-white'
            }`}
            onClick={() => setFilter(cat)}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* ── 3. IMAGE GRID WITH HOVER EXPAND ── */}
      <motion.div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" layout>
        <AnimatePresence>
          {filteredImages.map((img, i) => (
            <motion.div
              key={`${img.title}-${i}`}
              className="group relative h-72 rounded-3xl overflow-hidden shadow-2xl border border-white/10 hover:border-red-500/60 bg-[#161820] cursor-pointer transition-all duration-400"
              layout
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.35, delay: i * 0.03 }}
              onClick={() => setLightbox(img)}
              whileHover={{ y: -6 }}
            >
              {/* Image */}
              <img
                src={img.src}
                alt={img.title}
                loading="lazy"
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              />

              {/* Gradient Scrim */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent opacity-80 group-hover:opacity-95 transition-opacity p-5 flex flex-col justify-between" />

              {/* Top Category Badge */}
              <div className="absolute top-4 left-4 z-10">
                <span className="bg-red-600/90 backdrop-blur-md text-white text-[10px] font-black uppercase px-2.5 py-1 rounded-full shadow-md">
                  {img.category}
                </span>
              </div>

              {/* Quick Expand Icon */}
              <div className="absolute top-4 right-4 z-10 w-8 h-8 rounded-full bg-black/60 text-white flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity">
                <FaExpand />
              </div>

              {/* Bottom Information */}
              <div className="absolute bottom-4 left-4 right-4 z-10 flex flex-col gap-1">
                <h4 className="text-sm font-black text-white group-hover:text-red-400 transition-colors leading-snug">
                  {img.title}
                </h4>
                <div className="flex items-center gap-1.5 text-[10px] text-gray-300">
                  <FaMapMarkerAlt className="text-red-500" />
                  <span>{img.unit}</span>
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>

      {/* ── 4. LIGHTBOX MODAL ── */}
      <AnimatePresence>
        {lightbox && (
          <motion.div
            className="fixed inset-0 z-[2000] bg-black/90 backdrop-blur-lg flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setLightbox(null)}
          >
            <motion.div
              className="relative max-w-4xl w-full bg-[#161820] border border-red-500/40 rounded-3xl overflow-hidden shadow-2xl p-4 text-white"
              initial={{ scale: 0.85, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.85, y: 20 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative h-[65vh] w-full bg-[#0d0f14] rounded-2xl flex items-center justify-center overflow-hidden">
                <img
                  src={lightbox.src}
                  alt={lightbox.title}
                  className="w-full h-full object-contain"
                />
              </div>

              <div className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-t border-white/10 mt-3">
                <div>
                  <h3 className="text-lg font-black text-white">{lightbox.title}</h3>
                  <div className="flex items-center gap-3 text-xs text-gray-400 mt-0.5">
                    <span className="text-red-400 font-bold uppercase">{lightbox.category}</span>
                    <span>•</span>
                    <span>{lightbox.unit}</span>
                  </div>
                </div>

                <button
                  className="bg-red-600 hover:bg-red-500 text-white font-bold text-xs uppercase px-5 py-2.5 rounded-xl transition-all self-end sm:self-auto cursor-pointer"
                  onClick={() => setLightbox(null)}
                >
                  Close Preview
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Gallery;
